import React, { useEffect, useMemo, useState } from 'react';
import InlineImageUploader from './components/InlineImageUploader';
import RichTextEditor from './components/RichTextEditor';
import { slugify } from '../../hooks/blogHelpers';
import './BlogForm.css';
import FormActions from './components/FormActions';
import useConfirmNavigation from '../../hooks/useConfirmNavigation';

const defaultState = {
  title: '',
  slug: '',
  category: '',
  author: '',
  date: '',
  readTime: '',
  excerpt: '',
  image: '',
  content: '',
  htmlTitle: '',
  htmlMetaTags: '',
  tagsInput: '',
  topicIds: []
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const BlogForm = ({ initialData, topics = [], topicsLoading = false, onSubmit, onCancel, isSaving }) => {
  const [formState, setFormState] = useState(defaultState);
  const [isSlugDirty, setIsSlugDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const confirmationMessage = 'You have unsaved blog changes. Leave this form?';
  const submitLabel = initialData ? 'Update Blog' : 'Create Blog';

  useConfirmNavigation(!isSaving, confirmationMessage);

  useEffect(() => {
    if (!initialData) {
      setFormState({ ...defaultState });
      setIsSlugDirty(false);
      return;
    }
    setFormState({
      ...defaultState,
      title: initialData.title || '',
      slug: initialData.slug || '',
      category: initialData.category || '',
      author: initialData.author || '',
      date: toDateInputValue(initialData.date),
      readTime: initialData.readTime || '',
      excerpt: initialData.excerpt || '',
      image: initialData.image || '',
      content: initialData.content || '',
      htmlTitle: initialData.htmlTitle || '',
      htmlMetaTags: initialData.htmlMetaTags || '',
      tagsInput: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : '',
      topicIds: Array.isArray(initialData.topicIds) ? initialData.topicIds : []
    });
    setIsSlugDirty(Boolean(initialData.slug));
  }, [initialData]);

  const updateField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleTitleChange = (value) => {
    updateField('title', value);
    if (!isSlugDirty) {
      updateField('slug', slugify(value));
    }
  };

  const handleSlugChange = (value) => {
    setIsSlugDirty(true);
    updateField('slug', slugify(value));
  };

  const parsedTags = useMemo(() => {
    return formState.tagsInput
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length);
  }, [formState.tagsInput]);

  const handleTopicToggle = (topicId) => {
    setFormState((prev) => {
      const exists = prev.topicIds.includes(topicId);
      return {
        ...prev,
        topicIds: exists ? prev.topicIds.filter((id) => id !== topicId) : [...prev.topicIds, topicId]
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!onSubmit) return;
    const payload = {
      slug: slugify(formState.slug || formState.title),
      title: formState.title.trim(),
      category: formState.category.trim(),
      author: formState.author.trim(),
      date: formState.date ? new Date(formState.date).toISOString() : undefined,
      readTime: formState.readTime.trim(),
      excerpt: formState.excerpt.trim(),
      content: formState.content,
      image: formState.image,
      htmlTitle: formState.htmlTitle.trim(),
      htmlMetaTags: formState.htmlMetaTags.trim(),
      tags: parsedTags,
      topicIds: formState.topicIds
    };
    onSubmit(payload);
  };

  const handleCancelClick = () => {
    if (!window.confirm(confirmationMessage)) {
      return;
    }
    onCancel();
  };

  return (
    <form className="blog-form" onSubmit={handleSubmit}>
      <FormActions sticky isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />

      <div className="form-tabs" role="tablist">
        {[
          { key: 'basic', label: 'Basic Info' },
          { key: 'media', label: 'Media & Topics' },
          { key: 'excerpt', label: 'Excerpt' },
          { key: 'content', label: 'Content' },
          { key: 'seo', label: 'SEO' },
          { key: 'tags', label: 'Tags' }
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'basic' && (
        <div className="form-card">
          <h2>Basic Info</h2>
          <div className="form-group">
            <label>Title</label>
            <input value={formState.title} onChange={(event) => handleTitleChange(event.target.value)} required />
          </div>
          <div className="form-group">
            <label>Slug</label>
            <input
              value={formState.slug}
              onChange={(event) => handleSlugChange(event.target.value)}
              placeholder="e.g. building-design-systems"
              required
            />
            <p className="helper-text">Appears in the blog URL. Only lowercase letters, numbers, and hyphens.</p>
          </div>
          <div className="form-group">
            <label>Category</label>
            <input value={formState.category} onChange={(event) => updateField('category', event.target.value)} required />
          </div>
          <div className="form-group">
            <label>Author</label>
            <input value={formState.author} onChange={(event) => updateField('author', event.target.value)} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Publish Date</label>
              <input type="date" value={formState.date} onChange={(event) => updateField('date', event.target.value)} />
            </div>
            <div className="form-group">
              <label>Read Time</label>
              <input
                placeholder="e.g. 6 min read"
                value={formState.readTime}
                onChange={(event) => updateField('readTime', event.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'media' && (
        <div className="form-card">
          <h2>Media & Topics</h2>
          <div className="form-group">
            <label>Featured Image</label>
            <InlineImageUploader
              value={formState.image}
              onChange={(url) => updateField('image', url)}
              helperText="JPEG or PNG — minimum 1200px wide."
            />
          </div>
          <div className="form-group">
            <label>Topics</label>
            {topicsLoading ? (
              <p className="helper-text">Loading topics…</p>
            ) : topics.length === 0 ? (
              <p className="helper-text">No topics yet. Create some in "Blog Topics" first.</p>
            ) : (
              <div className="topics-grid topics-scrollable">
                {topics.map((topic) => {
                  const isSelected = formState.topicIds.includes(topic.id);
                  return (
                    <label key={topic.id} className={`topic-pill ${isSelected ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleTopicToggle(topic.id)}
                      />
                      <div className="topic-pill-content">
                        <span className="topic-pill-name">{topic.name}</span>
                        {topic.description && (
                          <span className="topic-pill-description">{topic.description}</span>
                        )}
                      </div>
                      <span className="topic-pill-meta">/{topic.slug || 'topic'}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'excerpt' && (
        <div className="form-card">
          <h2>Excerpt</h2>
          <div className="form-group">
            <label>Intro Text</label>
            <textarea
              value={formState.excerpt}
              onChange={(event) => updateField('excerpt', event.target.value)}
              rows={4}
              placeholder="Short summary shown on blog listings"
            />
          </div>
        </div>
      )}

      {activeTab === 'content' && (
        <div className="form-card">
          <h2>Content</h2>
          <RichTextEditor
            value={formState.content}
            onChange={(value) => updateField('content', value)}
            helperText="Powered by wangEditor — design visually or switch to HTML mode for direct markup."
          />
        </div>
      )}

      {activeTab === 'seo' && (
        <div className="form-card">
          <h2>SEO Metadata</h2>
          <div className="form-group">
            <label>HTML Title</label>
            <input
              value={formState.htmlTitle}
              onChange={(event) => updateField('htmlTitle', event.target.value)}
              placeholder="Custom <title> for the blog detail page"
            />
            <p className="helper-text">Keep under ~60 characters. Falls back to the blog title if left blank.</p>
          </div>
          <div className="form-group">
            <label>Meta Description</label>
            <textarea
              value={formState.htmlMetaTags}
              onChange={(event) => updateField('htmlMetaTags', event.target.value)}
              rows={3}
              placeholder="Optional description shown in SERPs/social cards"
            />
            <p className="helper-text">Aim for 150-160 characters so search engines can display the full text.</p>
          </div>
        </div>
      )}

      {activeTab === 'tags' && (
        <div className="form-card">
          <h2>Tags</h2>
          <div className="form-group">
            <label>Comma separated tags</label>
            <input
              value={formState.tagsInput}
              onChange={(event) => updateField('tagsInput', event.target.value)}
              placeholder="design, product, ui"
            />
            <p className="helper-text">Used for search and related posts.</p>
          </div>
        </div>
      )}

      <FormActions variant="footer" isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />
    </form>
  );
};

export default BlogForm;
